import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { DemoDialogComponent } from './demo-dialog.component';
import { formOptions, steps } from './form-data';
import { FormModel } from './form-model';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  @ViewChild(MatDrawer)
  drawer!: MatDrawer;

  currentStep = 0;
  maxStep = 1;

  links = [
    'Home',
    'About N-CLAS',
    'Users Guide',
    'Tutorial',
    'FAQ',
    'Glossary',
    'Provide Feedback',
  ];

  steps = steps;

  options = formOptions;

  area = new FormModel({ layer: '', name: '' });

  components: Record<string, boolean> = {};
  depositions: Record<string, boolean> = {};
  clTypes: Record<string, boolean> = {};
  allSpecies = false;
  species: Record<string, boolean> = {};
  outputs: Record<string, boolean> = {};

  speciesFilter = new FormControl('');

  filteredAreas!: Observable<string[]>;
  filteredSpecies!: Observable<string[]>;

  constructor(private dialog: MatDialog) {}

  ngOnInit(): void {
    this.filteredAreas = this.area.controls.name.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterOptions(this.options.areas.class1, value))
    );
    this.filteredSpecies = this.speciesFilter.valueChanges.pipe(
      startWith(''),
      map((value) => this.filterOptions(this.options.species.trees, value))
    );
  }

  changeStep(n: number) {
    this.currentStep = n;
    this.maxStep = Math.max(this.currentStep, this.maxStep);
  }

  getDemoStep(): number {
    if (this.area.controls.layer.value) {
      if (this.area.controls.name.value === 'Yellowstone NP') {
        return 2;
      }

      return 1;
    }

    return 0;
  }

  getStepSummary(n: number) {
    let summary = '';

    switch (n) {
      case 1:
        summary = this.area.controls.name.value;
        break;
      case 2:
        summary = this.summaryFromRecord(this.components);
        break;
      case 3:
        summary = this.summaryFromRecord(this.depositions);
        break;
      case 4:
        summary = this.summaryFromRecord(this.clTypes);
        break;
      case 5:
        summary = this.allSpecies
          ? 'All species'
          : this.summaryFromRecord(this.species);
        break;
      case 6:
        summary = '';
        break;
    }

    return summary.length > 32 ? summary.substring(0, 30) + '...' : summary;
  }

  isNextEnabled(n: number) {
    switch (n) {
      case 1:
        const v = this.area.value;
        return v.layer && this.options.areas.class1.includes(v.name);
      case 2:
        return this.validateFromRecord(this.components);
      case 3:
        return this.validateFromRecord(this.depositions);
      case 4:
        return this.validateFromRecord(this.clTypes);
      default:
        return true;
    }
  }

  isStepSkipped(n: number) {
    if (n == 4)
      return !(this.components['Trees'] || this.components['Lichens']);
    if (n == 5) return !this.components['Trees'];

    return false;
  }

  isStepCompleted(n: number) {
    return n < this.maxStep && !this.isStepSkipped(n);
  }

  isStepEnabled(n: number) {
    return n <= this.maxStep && !this.isStepSkipped(n);
  }

  openInfoPanel() {
    this.dialog.open(DemoDialogComponent, {
      data: {
        step: this.currentStep,
      },
    });
  }

  nextStep() {
    this.changeStep(this.currentStep + 1);
    if (this.isStepSkipped(this.currentStep)) this.nextStep();
  }

  refresh() {
    this.currentStep = 0;
    this.maxStep = 1;
    this.drawer.open();
  }

  private filterOptions(options: string[], value: string): string[] {
    const filterValue = value.toLowerCase();

    return options.filter(
      (option) => option.toLowerCase().indexOf(filterValue) >= 0
    );
  }

  private summaryFromRecord(r: Record<string, boolean>): string {
    return Object.keys(r)
      .filter((k) => r[k])
      .join(', ');
  }

  private validateFromRecord(r: Record<string, boolean>): boolean {
    return Object.keys(r).some((k) => r[k]);
  }
}
